import { todayPT } from "@homebound/activesupport";
import { type CardTag, Css, type TagProps } from "@homebound/beam";
import type { ReactNode } from "react";
import type { Maybe } from "src/generated/graphqlTypes";
import type { ExteriorOptionCard_CheckoutConfigOptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/components/ExteriorOptionCard.generated";
import type { OptionCardUtils_WouldDeselectFragment } from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils.generated";
import type { ProductOptionCard_CheckoutConfigPotentialOptionFragment } from "src/routes/projects/change-events/guided/workflows/components/ProductOptionCard.generated";
import { pluralize } from "src/utils";
import type { Temporal } from "temporal-polyfill";

/**
 * Tags to show next to specific options, keyed by `ReadyPlanOption` id, e.g. the true-up's "Discontinued" or "Removed".
 * Options without an entry show no tag.
 */
export type OptionTags = Record<string, CardTag>;

/** Only warn about cutoffs within the next 3 weeks, same as checkout does. */
const cutoffWarningDays = 21;

/** A `Tag` for an option group's cutoff, once it's near enough to matter; `undefined` otherwise. */
export function cutoffTag(cutoffDate: Maybe<Temporal.PlainDate>): TagProps<any> | undefined {
  if (!cutoffDate) return undefined;
  const days = todayPT().until(cutoffDate, { largestUnit: "day" }).days;
  if (days < 0) return { text: "Expired", type: "error" };
  if (days === 0) return { text: "Expires today", type: "warning" };
  if (days > cutoffWarningDays) return undefined;
  return { text: `${days} ${pluralize(days, "day")} to cutoff`, type: "warning" };
}

/**
 * Checkout's "Option Conflict" message for an option whose toggle would drop other selected options (the checkoutConfig's
 * `wouldDeselect`), i.e. "Selecting this 'H2 - English Cottage' will deselect the following option due to conflicts or
 * dependencies:" over a list of each dropped option with its group, i.e. "Sunlit Ivory (Exterior Scheme)"; `undefined`
 * when nothing would be dropped.
 */
export function conflictMessage(
  optionName: string,
  wouldDeselect: OptionCardUtils_WouldDeselectFragment[],
  action: "Selecting" | "Removing" = "Selecting",
): ReactNode | undefined {
  if (wouldDeselect.isEmpty) return undefined;
  return (
    <div>
      {action} this '{optionName}' will deselect the following {pluralize(wouldDeselect.length, "option")} due to
      conflicts or dependencies:
      <ul css={Css.m0.mt1.pl2.$}>
        {wouldDeselect.map((o) => (
          <li key={o.id}>
            {o.optionGroup.isSingleOptionGroup ? o.optionGroup.name : `${o.name} (${o.optionGroup.name})`}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The option's own image, else its primary product's (package groups shown as a single product have no `optionImage`). */
export function imageUrl(potential: ProductOptionCard_CheckoutConfigPotentialOptionFragment): string | undefined {
  return (
    potential.option.optionImage?.previewUrl ?? potential.primaryProduct?.featuredImage?.asset?.previewUrl ?? undefined
  );
}

/** Whether the group is an elevation or exterior scheme, i.e. gets the image cards. */
export function isExteriorGroup(group: ExteriorOptionCard_CheckoutConfigOptionGroupFragment): boolean {
  const { isElevation, isExteriorScheme } = group.group.group.type;
  return isElevation || isExteriorScheme;
}
